import React from "react";
import {useNavigate} from "react-router-dom";
import {capitalize} from "lodash";
import {Typography} from "@mui/material";
import {ProjectItemMenu} from "./ProjectItemMenu";
import {DeletedProjectMenu} from "./DeletedProjectMenu";
import {BrandProject} from "./constants";
import {
    getAssociationIds,
    CHANNEL_PILL_SX,
    getVideoCounts,
    labelChannelEntries,
} from "../helper";
import ListItemCard, {
    ListItemCardProps,
} from "../../../components/ListItemCard";
import {useUser} from "../../../hooks/useUser";
import {isExpert} from "../../../utils";
import {formatCurrency} from "../../../utils/formatCurrency";
import {PriceBreakdownTooltip} from "../../../components/Pricing/PriceBreakdownTooltip";
import { usePortalPath } from '../../../hooks/usePortalPath'

interface Props {
    project: BrandProject;
    onUpdate?: () => void;
    /**
     * A soft-deleted project (the projects page's Deleted view, admin / AM
     * admin). It offers Restore Project only: no edit / workflow menu, and no
     * click-through to the project page, which a deleted project no longer has.
     */
    deleted?: boolean;
}

const startCase = (text: string) =>
    text.replace(/\b\w+(?:'\w+)?\b/g, (word: string) => capitalize(word));

const ProjectListItem = ({deleted = false, ...rest}: Props) => {
    const {project} = rest;
    const navigate = useNavigate();
  const { p } = usePortalPath();
    const {role} = useUser();
    const showPrice = !isExpert(role);

    // Resolve product list: prefer products array, fall back to single product
    const products = (project as any)?.products as
        | Array<{image?: string; title?: string; desireid?: number}>
        | undefined;
    const primaryProduct =
        products && products.length > 0 ? products[0] : project?.product;
    const productCount =
        products && products.length > 0
            ? products.length
            : project?.product
            ? 1
            : 0;
    const productTitles =
        products && products.length > 0
            ? products
                  .map((p) => p.title)
                  .filter(Boolean)
                  .join(", ")
            : project?.product?.title || "";

    const getContentTypeLabel = () => {
        if (!project.content_type?.name) return null;
        const contentTypeLabel = startCase(
            project.content_type.name.replace(/_/i, " "),
        );
        const count = getVideoCounts(project).total;
        const countLabel = count === 1 ? "Video" : "Videos";
        return count > 0
            ? `${contentTypeLabel} - ${count} ${countLabel}`
            : contentTypeLabel;
    };

    const chips: ListItemCardProps["chips"] = [];
    const contentLabel = getContentTypeLabel();
    if (contentLabel) chips.push({label: contentLabel});
    if (project.content_channels && project.content_channels.length > 0) {
        // internal_status is internal-only (stripped server-side for brand
        // users), so appending it here needs no role check.
        labelChannelEntries(project.content_channels).forEach((ch: any) => {
            chips.push({
                label: ch.internal_status
                    ? `${ch.label}: ${ch.internal_status}`
                    : ch.label,
                chipSx: CHANNEL_PILL_SX,
            });
        });
    }
    return (
        <ListItemCard
            image={primaryProduct?.image}
            imageCount={productCount}
            title={project?.title || "Untitled Project"}
            subtitle={productTitles || undefined}
            associationIds={getAssociationIds(project)}
            brands={project.brands}
            brandsNavigable
            chips={chips}
            onClick={
                deleted
                    ? undefined
                    : () => navigate(p(`/video-production/projects/${project.id}?tab=details`))
            }
            rightContent={
                <>
                    {showPrice && (
                        <PriceBreakdownTooltip
                            breakdown={project.pricing_breakdown}
                            pricingSnapshottedAt={project.pricing_snapshotted_at}
                            project={project}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "CircularStd",
                                    fontWeight: 700,
                                    fontSize: "16px",
                                    color: "var(--text)",
                                    whiteSpace: "nowrap",
                                    cursor: project.pricing_breakdown ? "help" : "default",
                                }}
                            >
                                {formatCurrency(project.pricing_total)}
                            </Typography>
                        </PriceBreakdownTooltip>
                    )}
                    {deleted ? (
                        <DeletedProjectMenu
                            project={project}
                            onRestored={rest.onUpdate || (() => {})}
                        />
                    ) : (
                        <ProjectItemMenu
                            {...rest}
                            fetchProjects={rest.onUpdate || (() => {})}
                            onUpdate={rest.onUpdate}
                        />
                    )}
                </>
            }
        />
    );
};

export default ProjectListItem;
